import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const client = axios.create({ baseURL: API });

// Safely stringify an axios/FastAPI error so toasts never receive an object
// (React crashes when rendering pydantic validation arrays like
//  [{type, loc, msg, input, url}] as children).
export const apiErrorMsg = (err, fallback = "Something went wrong") => {
  const detail = err?.response?.data?.detail ?? err?.response?.data ?? err?.message;
  if (!detail) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((d) => (typeof d === "string" ? d : d?.msg || JSON.stringify(d)))
      .filter(Boolean)
      .join(" · ") || fallback;
  }
  if (typeof detail === "object") return detail.msg || JSON.stringify(detail);
  return String(detail);
};

// Public
export const fetchProjects = () => client.get("/projects").then((r) => r.data);
export const fetchArticles = () => client.get("/articles").then((r) => r.data);
export const fetchArticle  = (slug) => client.get(`/articles/${slug}`).then((r) => r.data);
export const fetchRoles    = () => client.get("/roles").then((r) => r.data);
export const fetchClientLogos = () => client.get("/client-logos").then((r) => r.data);

// Admin (token via X-Admin-Token)
const auth = (t) => ({ headers: { "X-Admin-Token": t } });

export const adminListProjects   = (t) => client.get("/admin/projects", auth(t)).then((r) => r.data);
export const adminCreateProject  = (t, p) => client.post("/admin/projects", p, auth(t)).then((r) => r.data);
export const adminUpdateProject  = (t, id, p) => client.put(`/admin/projects/${id}`, p, auth(t)).then((r) => r.data);
export const adminDeleteProject  = (t, id) => client.delete(`/admin/projects/${id}`, auth(t)).then((r) => r.data);

export const adminListArticles   = (t) => client.get("/admin/articles", auth(t)).then((r) => r.data);
export const adminCreateArticle  = (t, p) => client.post("/admin/articles", p, auth(t)).then((r) => r.data);
export const adminUpdateArticle  = (t, id, p) => client.put(`/admin/articles/${id}`, p, auth(t)).then((r) => r.data);
export const adminDeleteArticle  = (t, id) => client.delete(`/admin/articles/${id}`, auth(t)).then((r) => r.data);

export const adminListRoles      = (t) => client.get("/admin/roles", auth(t)).then((r) => r.data);
export const adminCreateRole     = (t, p) => client.post("/admin/roles", p, auth(t)).then((r) => r.data);
export const adminUpdateRole     = (t, id, p) => client.put(`/admin/roles/${id}`, p, auth(t)).then((r) => r.data);
export const adminDeleteRole     = (t, id) => client.delete(`/admin/roles/${id}`, auth(t)).then((r) => r.data);

export const adminListClientLogos   = (t) => client.get("/admin/client-logos", auth(t)).then((r) => r.data);
export const adminCreateClientLogo  = (t, p) => client.post("/admin/client-logos", p, auth(t)).then((r) => r.data);
export const adminUpdateClientLogo  = (t, id, p) => client.put(`/admin/client-logos/${id}`, p, auth(t)).then((r) => r.data);
export const adminDeleteClientLogo  = (t, id) => client.delete(`/admin/client-logos/${id}`, auth(t)).then((r) => r.data);

// ─────────── CRM (Reachvel Dashboard) ───────────
const crm = "/admin/crm";

export const crmAnalytics = (t) => client.get(`${crm}/analytics`, auth(t)).then((r) => r.data);

// Generic CRUD helpers — pass entity path e.g. "leads", "vendors", "projects", "tasks",
// "expenses", "vendor-payments", "invoices", "project-payments", "reachvel-payments".
export const crmList   = (t, entity, params = {}) => client.get(`${crm}/${entity}`, { ...auth(t), params }).then((r) => r.data);
export const crmGet    = (t, entity, id) => client.get(`${crm}/${entity}/${id}`, auth(t)).then((r) => r.data);
export const crmCreate = (t, entity, payload) => client.post(`${crm}/${entity}`, payload, auth(t)).then((r) => r.data);
export const crmUpdate = (t, entity, id, payload) => client.put(`${crm}/${entity}/${id}`, payload, auth(t)).then((r) => r.data);
export const crmDelete = (t, entity, id) => client.delete(`${crm}/${entity}/${id}`, auth(t)).then((r) => r.data);

export const crmConvertLead = (t, leadId) => client.post(`${crm}/leads/${leadId}/convert`, {}, auth(t)).then((r) => r.data);
export const crmRecordInvoicePayment = (t, iid, payload) =>
  client.post(`${crm}/invoices/${iid}/record-payment`, payload, auth(t)).then((r) => r.data);

export const crmUploadInvoiceFile = (t, iid, file) => {
  const fd = new FormData();
  fd.append("file", file);
  return client.post(`${crm}/invoices/${iid}/upload`, fd, {
    headers: { ...auth(t).headers, "Content-Type": "multipart/form-data" },
  }).then((r) => r.data);
};
export const crmDeleteInvoiceFile = (t, iid) =>
  client.delete(`${crm}/invoices/${iid}/file`, auth(t)).then((r) => r.data);
export const crmInvoiceFileUrl = (iid) => `${process.env.REACT_APP_BACKEND_URL}/api/admin/crm/invoices/${iid}/file`;

// ─── Admin: Clients management ───
export const adminListClients   = (t) => client.get("/admin/clients", auth(t)).then((r) => r.data);
export const adminCreateClient  = (t, p) => client.post("/admin/clients", p, auth(t)).then((r) => r.data);
export const adminUpdateClient  = (t, id, p) => client.put(`/admin/clients/${id}`, p, auth(t)).then((r) => r.data);
export const adminDeleteClient  = (t, id) => client.delete(`/admin/clients/${id}`, auth(t)).then((r) => r.data);

// ─── Client portal API ───
const cauth = (t) => ({ headers: { Authorization: `Bearer ${t}` } });
const cauthFin = (t, ft) => ({ headers: { Authorization: `Bearer ${t}`, "X-Finance-Token": ft } });
export const clientLogin           = (username, password) => client.post("/client/login", { username, password }).then((r) => r.data);
export const clientMe              = (t) => client.get("/client/me", cauth(t)).then((r) => r.data);
export const clientFinanceUnlock   = (t, password) => client.post("/client/finance-unlock", { password }, cauth(t)).then((r) => r.data);
export const clientFetchProjects   = (t) => client.get("/client/projects", cauth(t)).then((r) => r.data);
export const clientFetchInvoices   = (t, ft) => client.get("/client/invoices", cauthFin(t, ft)).then((r) => r.data);
export const clientFetchPayments   = (t, ft) => client.get("/client/payments", cauthFin(t, ft)).then((r) => r.data);
export const clientFetchSummary    = (t, ft) => client.get("/client/summary", cauthFin(t, ft)).then((r) => r.data);
export const clientFetchComments   = (t, pid) => client.get(`/client/projects/${pid}/comments`, cauth(t)).then((r) => r.data);
export const clientAddComment      = (t, pid, body, url = "") => client.post(`/client/projects/${pid}/comments`, { body, url }, cauth(t)).then((r) => r.data);

// Admin: comments + audit
export const adminFetchComments    = (t, pid) => client.get(`/admin/projects/${pid}/comments`, auth(t)).then((r) => r.data);
export const adminAddComment       = (t, pid, body, url = "") => client.post(`/admin/projects/${pid}/comments`, { body, url }, auth(t)).then((r) => r.data);
export const adminDeleteComment    = (t, pid, cid) => client.delete(`/admin/projects/${pid}/comments/${cid}`, auth(t)).then((r) => r.data);
export const adminFetchAuditLog    = (t, params = {}) => client.get(`/admin/audit-log`, { ...auth(t), params }).then((r) => r.data);

// Summaries / aggregates / sync
export const crmLeadsSummary    = (t) => client.get(`${crm}/leads/summary`, auth(t)).then((r) => r.data);
export const crmVendorsSummary  = (t) => client.get(`${crm}/vendors/summary`, auth(t)).then((r) => r.data);
export const crmProjectsSummary = (t) => client.get(`${crm}/projects/summary`, auth(t)).then((r) => r.data);
export const crmProjectsAggregates = (t) => client.get(`${crm}/projects/aggregates`, auth(t)).then((r) => r.data);
export const crmReachvelSummary = (t) => client.get(`${crm}/reachvel-payments/summary`, auth(t)).then((r) => r.data);
export const crmOrphansCheck    = (t) => client.get(`${crm}/orphans/check`, auth(t)).then((r) => r.data);
export const crmOrphansCleanup  = (t) => client.post(`${crm}/orphans/cleanup`, {}, auth(t)).then((r) => r.data);
export const crmBackfillWebsiteStatus = (t) => client.post(`${crm}/projects/backfill-website-status`, {}, auth(t)).then((r) => r.data);

// ─── Admin: Change Requests ───
export const crmListChangeRequests = (t, params = {}) => client.get(`${crm}/change-requests`, { ...auth(t), params }).then((r) => r.data);
export const crmCreateChangeRequest = (t, p) => client.post(`${crm}/change-requests`, p, auth(t)).then((r) => r.data);
export const crmUpdateChangeRequest = (t, id, p) => client.put(`${crm}/change-requests/${id}`, p, auth(t)).then((r) => r.data);
export const crmDeleteChangeRequest = (t, id) => client.delete(`${crm}/change-requests/${id}`, auth(t)).then((r) => r.data);
export const crmUploadCRDocument = (t, id, file) => {
  const fd = new FormData();
  fd.append("file", file);
  return client.post(`${crm}/change-requests/${id}/documents`, fd, {
    headers: { ...auth(t).headers, "Content-Type": "multipart/form-data" },
  }).then((r) => r.data);
};
export const crmDeleteCRDocument = (t, id, docId) => client.delete(`${crm}/change-requests/${id}/documents/${docId}`, auth(t)).then((r) => r.data);
export const crmCRDocumentUrl = (id, docId) => `${API}/admin/crm/change-requests/${id}/documents/${docId}`;

// ─── Client: Change Requests (regular JWT, NOT finance-locked) ───
export const clientFetchChangeRequests = (t) => client.get("/client/change-requests", cauth(t)).then((r) => r.data);
export const clientCreateChangeRequest = (t, p) => client.post("/client/change-requests", p, cauth(t)).then((r) => r.data);
export const clientUploadCRDocument = (t, id, file) => {
  const fd = new FormData();
  fd.append("file", file);
  return client.post(`/client/change-requests/${id}/documents`, fd, {
    headers: { ...cauth(t).headers, "Content-Type": "multipart/form-data" },
  }).then((r) => r.data);
};
export const clientCRDocumentUrl = (id, docId) => `${API}/client/change-requests/${id}/documents/${docId}`;

// ─── CR Comments — two-way thread (admin + client) ───
export const crmFetchCRComments  = (t, crId) => client.get(`${crm}/change-requests/${crId}/comments`, auth(t)).then((r) => r.data);
export const crmAddCRComment     = (t, crId, body_html) => client.post(`${crm}/change-requests/${crId}/comments`, { body_html }, auth(t)).then((r) => r.data);
export const crmDeleteCRComment  = (t, crId, commentId) => client.delete(`${crm}/change-requests/${crId}/comments/${commentId}`, auth(t)).then((r) => r.data);
export const crmToggleCRReaction = (t, crId, commentId, emoji) => client.post(`${crm}/change-requests/${crId}/comments/${commentId}/reactions`, { emoji }, auth(t)).then((r) => r.data);
export const clientFetchCRComments = (t, crId) => client.get(`/client/change-requests/${crId}/comments`, cauth(t)).then((r) => r.data);
export const clientAddCRComment    = (t, crId, body_html) => client.post(`/client/change-requests/${crId}/comments`, { body_html }, cauth(t)).then((r) => r.data);
export const clientToggleCRReaction = (t, crId, commentId, emoji) => client.post(`/client/change-requests/${crId}/comments/${commentId}/reactions`, { emoji }, cauth(t)).then((r) => r.data);

// ─── Client: Maintenance projects (finance-locked) ───
export const clientFetchMaintenance = (t, ft) => client.get("/client/maintenance-projects", cauthFin(t, ft)).then((r) => r.data);

// ─── Admin: Payment Receipts ───
export const crmListReceipts   = (t, params = {}) => client.get(`${crm}/payment-receipts`, { ...auth(t), params }).then((r) => r.data);
export const crmCreateReceipt  = (t, p) => client.post(`${crm}/payment-receipts`, p, auth(t)).then((r) => r.data);
export const crmUpdateReceipt  = (t, id, p) => client.put(`${crm}/payment-receipts/${id}`, p, auth(t)).then((r) => r.data);
export const crmDeleteReceipt  = (t, id) => client.delete(`${crm}/payment-receipts/${id}`, auth(t)).then((r) => r.data);
export const crmUploadReceiptFile = (t, id, file) => {
  const fd = new FormData();
  fd.append("file", file);
  return client.post(`${crm}/payment-receipts/${id}/upload`, fd, {
    headers: { ...auth(t).headers, "Content-Type": "multipart/form-data" },
  }).then((r) => r.data);
};
export const crmDeleteReceiptFile = (t, id) => client.delete(`${crm}/payment-receipts/${id}/file`, auth(t)).then((r) => r.data);
export const crmReceiptFileUrl = (id) => `${API}/admin/crm/payment-receipts/${id}/file`;

// ─── Admin: Non-Project Payments (additional-work invoices) ───
const np = `${crm}/non-project-invoices`;
export const crmListNonProject   = (t, params = {}) => client.get(np, { ...auth(t), params }).then((r) => r.data);
export const crmCreateNonProject = (t, p) => client.post(np, p, auth(t)).then((r) => r.data);
export const crmUpdateNonProject = (t, id, p) => client.put(`${np}/${id}`, p, auth(t)).then((r) => r.data);
export const crmDeleteNonProject = (t, id) => client.delete(`${np}/${id}`, auth(t)).then((r) => r.data);
export const crmRecordNonProjectPayment = (t, id, p) => client.post(`${np}/${id}/record-payment`, p, auth(t)).then((r) => r.data);
export const crmDeleteNonProjectPayment = (t, id, payId) => client.delete(`${np}/${id}/payments/${payId}`, auth(t)).then((r) => r.data);
export const crmUploadNonProjectFile = (t, id, file) => {
  const fd = new FormData();
  fd.append("file", file);
  return client.post(`${np}/${id}/upload`, fd, {
    headers: { ...auth(t).headers, "Content-Type": "multipart/form-data" },
  }).then((r) => r.data);
};
export const crmDeleteNonProjectFile = (t, id) => client.delete(`${np}/${id}/file`, auth(t)).then((r) => r.data);
export const crmNonProjectFileUrl = (id) => `${API}${np}/${id}/file`;
export const clientFetchNonProject = (t, ft) => client.get("/client/non-project-invoices", cauthFin(t, ft)).then((r) => r.data);
export const clientNonProjectFileUrl = (id) => `${API}/client/non-project-invoices/${id}/file`;

// ─── Client: Payment Receipts (finance-locked) ───
export const clientFetchReceipts = (t, ft) => client.get("/client/payment-receipts", cauthFin(t, ft)).then((r) => r.data);
export const clientReceiptFileUrl = (id) => `${API}/client/payment-receipts/${id}/file`;
